import { createApp, nextTick } from 'vue';
// i18n
import { DynamicScroller, DynamicScrollerItem, RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

import { createI18n } from 'vue-i18n';

import { setupCalendar, Calendar, DatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import { screens } from 'vue-screen-utils';
// import locale from 'element-plus/lib/locale/lang/zh-cn' // 中文语言
import moment from 'moment';
import lodash from 'lodash';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faLanguage } from '@fortawesome/free-solid-svg-icons';

import ja from './lang/ja';
import zh from './lang/zh';

import 'element-plus/theme-chalk/dark/css-vars.css'; // Element Plus 暗色模式变量（配合 html.dark 生效）
import '@/assets/styles/index.scss'; // global css

import App from './App';
import store from './store';
import router from './router';
import directive from './directive'; // directive

// 注册指令
import plugins from './plugins'; // plugins
import { download } from '@/utils/request';
// svg图标
import 'virtual:svg-icons-register';
import SvgIcon from '@/components/SvgIcon';
import elementIcons from '@/components/SvgIcon/svgicon';
import './permission'; // permission control

import { useDict } from '@/utils/dict';
import { isNumber } from '@/utils/reg';
import {
  validateNumber,
  validateNumberRequired,
  validateEmpty,
  validateLongitude,
  validateLatitude,
  validatePhone,
  isValidDate,
  validCode,
  checkVersionAndUpdateStorage,
} from '@/utils/common';
import {
  parseTime, resetForm, addDateRange, handleTree, selectDictLabel, selectDictLabels,
} from '@/utils/ruoyi';

// 分页组件
import Pagination from '@/components/Pagination';
// 自定义表格工具组件
import RightToolbar from '@/components/RightToolbar';
// 富文本组件
import Editor from '@/components/Editor';
// 文件上传组件
import FileUpload from '@/components/FileUpload';
// 图片上传组件
import ImageUpload from '@/components/ImageUpload';
// 图片预览组件
import ImagePreview from '@/components/ImagePreview';
// 自定义树选择组件
import TreeSelect from '@/components/TreeSelect';
// 字典标签组件
import DictTag from '@/components/DictTag';

// 自定义TableColumn组件
import CusTableColumn from '@/components/Common/CusTableColumn';
// 自定义Select组件
import CusSelect from '@/components/Common/CusSelect';
import PipeTag from '@/components/PipeTag';
// 图像上传组件（带拖拽和排序功能）
import ImageSortUpload from '@/components/ImageSortUpload';
// LazyLoad Select组件
import LazyLoadSelect from '@/components/LazyLoadSelect/';
// KeepResult Select组件
import KeepResultSelect from '@/components/Common/KeepResultSelect';
// // Status Nofitication组件
// import StatusNofitication from '@/components/StatusNofitication/';
// // Search Type组件
// import SearchType from '@/components/Common/SearchType';
// // Merge History组件
// import MergeHistory from '@/components/Common/MergeHistory.vue';

const i18n = createI18n({
  legacy: false,
  locale: 'ja',
  messages: {
    ja,
    zh,
  },
});

const app = createApp(App);

// 全局方法挂载
app.config.globalProperties.useDict = useDict;
app.config.globalProperties.download = download;
app.config.globalProperties.parseTime = parseTime;
app.config.globalProperties.resetForm = resetForm;
app.config.globalProperties.handleTree = handleTree;
app.config.globalProperties.addDateRange = addDateRange;
app.config.globalProperties.selectDictLabel = selectDictLabel;
app.config.globalProperties.selectDictLabels = selectDictLabels;
app.config.globalProperties.$moment = moment;
// 验证是否为数字
app.config.globalProperties.$isNumber = isNumber;
// 数字验证方法
app.config.globalProperties.$validateNumber = validateNumber;
// 数字必填验证方法
app.config.globalProperties.$validateNumberRequired = validateNumberRequired;
// 空值验证方法
app.config.globalProperties.$validateEmpty = validateEmpty;
// 经度验证方法
app.config.globalProperties.$validateLongitude = validateLongitude;
// 纬度验证方法
app.config.globalProperties.$validateLatitude = validateLatitude;
// 电话号码验证方法
app.config.globalProperties.$validatePhone = validatePhone;
// 是否日期格式
app.config.globalProperties.$isValidDate = isValidDate;
// 非法字符验证方法
app.config.globalProperties.$validCode = validCode;
// lodash
app.config.globalProperties.$_ = lodash;

// 全局组件挂载
app.component('DictTag', DictTag);
app.component('Pagination', Pagination);
app.component('TreeSelect', TreeSelect);
app.component('FileUpload', FileUpload);
app.component('ImageUpload', ImageUpload);
app.component('ImagePreview', ImagePreview);
app.component('RightToolbar', RightToolbar);
app.component('Editor', Editor);
app.component('ImageSortUpload', ImageSortUpload);
app.component('CusTableColumn', CusTableColumn);
app.component('VCalendar', Calendar);
app.component('VDatePicker', DatePicker);
app.component('CusSelect', CusSelect);
app.component('PipeTag', PipeTag);
app.component('svg-icon', SvgIcon);
app.component('DynamicScroller', DynamicScroller);
app.component('DynamicScrollerItem', DynamicScrollerItem);
app.component('RecycleScroller', RecycleScroller);
app.component('LazyLoadSelect', LazyLoadSelect);
app.component('KeepResultSelect', KeepResultSelect);
// app.component('StatusNofitication', StatusNofitication);
// app.component('MergeHistory', MergeHistory);

// fortawesome
library.add(faLanguage);
// update localStorage
checkVersionAndUpdateStorage();
app.component('font-awesome-icon', FontAwesomeIcon);

app.use(router);
app.use(store);
app.use(plugins);
app.use(elementIcons);
app.use(i18n);
app.use(setupCalendar, {});
app.use(screens, {
  mobile: '0px',
  tablet: '640px',
  laptop: '1024px',
  desktop: '1280px',
});
directive(app);
app.directive('load-more', {
  updated(el, binding) {
    const trigger = el.querySelector('.select-trigger');
    const id = trigger.getAttribute('aria-describedby');
    if (id) {
      const popper = document.getElementById(id);
      const selectWrap = popper.querySelector(
        '.el-scrollbar .el-select-dropdown__wrap',
      );
      selectWrap.addEventListener('scroll', () => {
        const { scrollHeight, scrollTop, clientHeight } = selectWrap;
        const conditon = scrollHeight - scrollTop - clientHeight;
        if (conditon < 100) {
          binding.value();
        }
      });
    }
  },
});
app.directive('ctrl-enter', {
  mounted(el, binding) {
    const handler = (event) => {
      if (event.ctrlKey && event.key === 'Enter') {
        binding.value();
      }
    };
    el.__ctrlEnterHandler__ = handler;

    // 添加事件监听器
    el.addEventListener('keydown', handler);
  },
  // 当指令被卸载时...
  unmounted(el) {
    // 移除事件监听器
    el.removeEventListener('keydown', el.__ctrlEnterHandler__);
    // 清除引用
    el.__ctrlEnterHandler__ = null;
  },
});
app.mount('#app');
